import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";
import { CircleCheck, Link2 } from "lucide-react";
import { useState, type FormEvent } from "react";

import CustomerScreenShell from "@/components/customer-screen-shell";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { BRAND_NAME } from "@buildpy/ui/brand-paths";
import { connectReady } from "@buildpy/core/modules/project/connect-code-shape";
import { connectProjectByCode } from "@/api/actions/project-connect";
import { settleRedirect } from "@/lib/redirect-error";
import { isNativeApp } from "@/lib/native-bridge";
import { rememberCustomerLink } from "@/modules/project/ui/customer-links";
import { browserDraftStore } from "@/modules/project/ui/log-draft";

export const metadata: Metadata = {
  title: "현장 연결",
  robots: { index: false, follow: false },
};

const FAILED = "연결하지 못했습니다. 잠시 뒤 다시 시도해 주세요";

export interface Connected {
  path: string;
  org_name: string;
  customer_name: string;
}

// 「내 현장」 경로 끝(/project)을 「우리 집 기록」(/house)으로.
export const housePathOf = (path: string): string => path.replace(/\/project$/, "/house");

interface Miss {
  message: string;
  remaining: number | null;
  locked: boolean;
}

const Done = ({ done }: { done: Connected }) => (
  <SectionCard className="flex flex-col gap-y-4" data-testid="connect-done" role="status">
    <div className="flex flex-col items-center gap-y-2 text-center">
      <CircleCheck aria-hidden className="size-10 text-primary" />
      <p className="text-sm text-muted-foreground">{done.org_name}</p>
      <p className="text-lg font-semibold">
        {done.customer_name ? `${done.customer_name} 고객님 댁 공사가 연결됐어요` : "공사가 연결됐어요"}
      </p>
      <p className="text-sm text-muted-foreground">현장 코드로 연결했어요</p>
    </div>
    <ul className="flex list-disc flex-col gap-y-1 pl-5 text-sm text-muted-foreground">
      <li>이 기기·브라우저에서 공사 화면 주소를 다시 열면 확인 없이 열려요 — 즐겨찾기해 두세요</li>
      <li>다른 기기·브라우저이거나 링크 기간이 지나면 링크나 현장 코드로 다시 연결해요</li>
    </ul>
    <div className="flex flex-col gap-y-2">
      <Button asChild size="cta" variant="primary">
        <Link data-testid="connect-go" href={done.path}>
          공사 보러 가기
        </Link>
      </Button>
      <Button asChild size="lg" variant="outline">
        <Link data-testid="connect-house" href={housePathOf(done.path)}>
          우리 집 기록 보기
        </Link>
      </Button>
    </div>
  </SectionCard>
);

// 고객 「현장 연결」(로그인 없음) — 현장 코드 + 휴대폰 끝 4자리 → 입장 쿠키 · 5번 틀리면 잠금(서버가 센다).
const Page = ({ initial = null }: { initial?: Connected | null }) => {
  const [code, setCode] = useState("");
  const [last4, setLast4] = useState("");
  const [pending, setPending] = useState(false);
  const [miss, setMiss] = useState<Miss | null>(null);
  const [done, setDone] = useState<Connected | null>(initial);
  const locked = miss?.locked === true;
  const ready = connectReady(code, last4) && !locked && !pending;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setPending(true);
    try {
      const result = await connectProjectByCode({ code, last4 });
      if (result.ok) {
        setMiss(null);
        // 앱 안에서만 이 기기에 기억한다 — 다음에 앱을 열면 첫 화면(/start)이 바로 이 공사로 보낸다.
        if (isNativeApp())
          rememberCustomerLink(browserDraftStore(), {
            token: result.token,
            path: result.path,
            orgName: result.org_name,
            customerName: result.customer_name,
            savedAt: Date.now(),
          });
        setDone({ path: result.path, org_name: result.org_name, customer_name: result.customer_name });
        return;
      }
      setMiss({ message: result.message, remaining: result.remaining ?? null, locked: result.locked === true });
      setLast4("");
    } catch (e) {
      if (settleRedirect(e)) return;
      setMiss({ message: FAILED, remaining: null, locked: false });
    } finally {
      setPending(false);
    }
  };

  return (
    <CustomerScreenShell>
      <div className="mx-auto flex w-full max-w-md flex-col gap-y-4">
        {done ? (
          <Done done={done} />
        ) : (
          <>
            <div className="flex flex-col gap-y-1">
              <p className="text-sm font-semibold text-primary">{BRAND_NAME}</p>
              <h1 className="text-xl font-semibold">공사 현장 연결하기</h1>
              <p className="text-sm text-muted-foreground">비밀번호는 없어요. 둘 중 편한 방법으로 연결하세요</p>
            </div>

            <SectionCard className="flex flex-col gap-y-1 text-sm" data-testid="connect-link-guide">
              <p className="flex items-center gap-x-1.5 font-semibold">
                <Link2 aria-hidden className="size-4" />
                문자로 받은 링크로
              </p>
              <p className="text-muted-foreground">시공사가 보낸 문자 링크를 누르면 따로 입력할 것 없이 공사 화면이 열려요</p>
            </SectionCard>

            <p className="text-center text-xs text-muted-foreground">또는</p>

            <SectionCard as="form" className="flex flex-col gap-y-3" data-testid="connect-form" onSubmit={(e: FormEvent) => void submit(e)}>
              <div className="flex flex-col gap-y-0.5">
                <p className="text-sm font-semibold">현장 코드로</p>
                <p className="text-xs text-muted-foreground">계약서 첫 장이나 문자에 적힌 코드예요</p>
              </div>
              <Input
                autoCapitalize="characters"
                autoComplete="off"
                data-testid="connect-code"
                disabled={pending || locked}
                inputSize="lg"
                label="현장 코드"
                maxLength={20}
                name="connect-code"
                placeholder="예: AB12CD34"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
              <Input
                autoComplete="off"
                data-testid="connect-last4"
                disabled={pending || locked}
                inputMode="numeric"
                inputSize="lg"
                label="휴대폰 번호 뒷 4자리"
                maxLength={4}
                name="connect-last4"
                placeholder="0000"
                value={last4}
                onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
              />
              {miss && (
                <p className={locked ? "text-sm text-destructive" : "text-sm text-warning"} data-testid="connect-miss" role="alert">
                  {miss.message}
                  {!locked && miss.remaining !== null && ` · 남은 횟수 ${miss.remaining}회`}
                  {locked && " · 담당자에게 전화로 물어보셔도 돼요"}
                </p>
              )}
              <Button className="w-full" data-testid="connect-submit" disabled={!ready} size="cta" type="submit" variant="primary">
                {pending ? "확인하는 중" : "연결하기"}
              </Button>
            </SectionCard>

          </>
        )}
      </div>
    </CustomerScreenShell>
  );
};

export default Page;
